import { useLayoutEffect, useMemo, type ReactNode, type Ref } from "react";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { useInertRef } from "@hooks/useInertRef";
import { Scrollbar } from "@design-system/Scrollbar/Scrollbar";
import { TooltipProvider } from "@design-system/Tooltip/Tooltip";
import { styleWithVars } from "@lib/cssVars";
import {
    useOptionalSideNavContext,
    useSideNavMode,
    useSideNavWidth,
} from "./SideNavProvider";
import { SideNavResizeHandle } from "./SideNavResizeHandle";
import { DEFAULT_OPEN_WIDTH, MINI_WIDTH } from "./sideNavWidth";
import type {
    SideNavClassNames,
    SideNavMode,
    SideNavProps,
} from "./SideNav.types";
import styles from "./SideNav.module.scss";

export type { SideNavClassNames, SideNavProps } from "./SideNav.types";

const regionClassName = (
    name: keyof SideNavClassNames,
    overrides: SideNavClassNames | undefined,
) => classNames(styles.region, styles[name], overrides?.[name]);

const SideNavRegion = ({
    className,
    children,
}: {
    className: string;
    children: ReactNode;
}) => (children == null ? null : <div className={className}>{children}</div>);

const dockedWidthOf = ({
    isHidden,
    mode,
}: {
    isHidden: boolean;
    mode: SideNavMode;
}) => {
    if (isHidden) {
        return "0px";
    }
    return mode === "mini"
        ? `${MINI_WIDTH}px`
        : `var(--side-nav-width, ${DEFAULT_OPEN_WIDTH}px)`;
};

/**
 * Publishes the width the nav docks on the root as `--docked-nav-width`, for
 * overlays anchored to the viewport (full-width drawers) to start after it.
 * Set from here rather than with `:root:has(.sideNav)`: Chromium re-runs that
 * rule over the whole document on every focus move.
 */
const usePublishedDockedWidth = (placement: {
    isHidden: boolean;
    mode: SideNavMode;
}) => {
    const dockedWidth = dockedWidthOf(placement);
    useLayoutEffect(() => {
        const root = document.documentElement;
        root.style.setProperty("--docked-nav-width", dockedWidth);
        return () => {
            root.style.removeProperty("--docked-nav-width");
        };
    }, [dockedWidth]);
};

/** Mode and visibility from the nearest provider: open and shown without one. */
const useSideNavPlacement = (ref: Ref<HTMLElement> | undefined) => {
    const context = useOptionalSideNavContext();
    const mode = useSideNavMode();
    const width = useSideNavWidth();
    const isHidden = context?.isHidden ?? false;
    usePublishedDockedWidth({ isHidden, mode });
    // On the nav itself, not only the root, so a freshly mounted nav is first
    // laid out at the saved width instead of the 220px fallback, which would
    // animate to it.
    const style =
        context === null
            ? undefined
            : styleWithVars({ "--side-nav-width": `${width}px` });
    const setInert = useInertRef(isHidden);
    const contextNavRef = context?.navRef;
    const navRef = useMemo(
        () => mergeRefs([ref, setInert, contextNavRef]),
        [ref, setInert, contextNavRef],
    );

    return { isHidden, mode, navRef, style };
};

/**
 * Main navigation column (Figma `SideNav`): header, top rows that never
 * scroll, a scrolling body of sections and a footer. Inside a
 * `SideNavProvider` it is open (220px by default, resizable) or mini (56px),
 * switched and resized from its right edge, and hides by sliding off to the
 * left (its column collapses with it); without one it is always open.
 */
export const SideNav = ({
    "aria-label": ariaLabel,
    header,
    top,
    footer,
    children,
    classNames: regionClassNames,
    className,
    ref,
}: SideNavProps) => {
    const { isHidden, mode, navRef, style } = useSideNavPlacement(ref);

    return (
        <nav
            ref={navRef}
            aria-label={ariaLabel}
            className={classNames(styles.sideNav, className)}
            style={style}
            data-mode={mode}
            data-hidden={isHidden || undefined}
        >
            <TooltipProvider>
                <SideNavRegion
                    className={regionClassName("header", regionClassNames)}
                >
                    {header}
                </SideNavRegion>
                <SideNavRegion
                    className={regionClassName("top", regionClassNames)}
                >
                    {top}
                </SideNavRegion>
                <Scrollbar
                    className={regionClassName("body", regionClassNames)}
                    showFadeIndicators={false}
                >
                    <div className={styles.bodyContent}>{children}</div>
                </Scrollbar>
                <SideNavRegion
                    className={regionClassName("footer", regionClassNames)}
                >
                    {footer}
                </SideNavRegion>
            </TooltipProvider>
            <SideNavResizeHandle />
        </nav>
    );
};
